import {
  CFormCheck,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { PercentIcon, QrCodeIcon } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import DeleteIcon from "../../components/icons/DeleteIcon";
import EditIcon from "../../components/icons/EditIcon";
import TranslateIcon from "../../components/icons/TranslateIcon";
import PromptModal from "../../components/modals/PromptModal";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import { OFFER_TYPES } from "../../helpers/enums";
import { useAuth } from "../../hooks/useAuth";
import { useCookie } from "../../hooks/useCookie";
import { useSelectedPlace } from "../../hooks/place/useSelectedPlace";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import { iconBtnStyle, tdStyle, thStyle } from "../../helpers/constants";
import { Chip, IconAction } from "../../helpers/general";

const getOfferUpdateLink = (offer: { id: string; type: string }) => {
  if (offer.type === OFFER_TYPES.SPECIAL) {
    return `/offers/update/special-offer/${offer.id}`;
  } else if (offer.type === OFFER_TYPES.EVENT) {
    return `/offers/update/event-offer/${offer.id}`;
  } else if (offer.type === OFFER_TYPES.TOUR) {
    return `/offers/update/tour-offer/${offer.id}`;
  }
  return "";
};

export const offersDisplayType: { [key: string]: string } = {
  SPECIAL_OFFER: "Offre spéciale",
  TOUR_OFFER: "Offre de tourisme",
  EVENT_OFFER: "Offre d'événement",
};

/* ─────────────── Row ─────────────── */

function TableBodyRowContent({
  Offer,
  deletedList,
}: {
  Offer: SpecialOffers;
  deletedList: React.Dispatch<BulkDeleteActions>;
}) {
  const [isChecked, setIsChecked] = useState(false);
  const { toastSuccess, toastError } = useToast();
  const { token } = useCookie("vToken");
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const { isAdmin, isMultiPlaceAdmin, user } = useAuth();
  const { selectedPlaceId } = useSelectedPlace(user, isAdmin);

  const deleteOffer = useMutation((id: string) => {
    return apiService.MakeDeleteRequest(
      `specials/${id}?placeId=${selectedPlaceId}`,
      token,
    );
  });
  const updateOfferStatus = useMutation((url: string) => {
    return apiService.MakePutRequest(`${url}`, {}, token);
  });

  useEffect(() => {
    if (isChecked) {
      deletedList({ type: DELETE_ACTIONS.ADD, id: Offer.id });
    } else {
      deletedList({ type: DELETE_ACTIONS.REMOVE, id: Offer.id });
    }
  }, [deletedList, isChecked, Offer.id]);

  const removeOffer = () => {
    deleteOffer.mutate(Offer.id, {
      onError(error) {
        console.log(error);
        toastError(general.fr.message.operationFailed);
      },
      onSuccess() {
        toastSuccess(general.fr.message.offerDeleted);
        queryClient.invalidateQueries("getAllOffers");
      },
    });
  };

  const approveOffer = (id: string) => {
    updateOfferStatus.mutate(`specials/${id}/approve`, {
      onSuccess() {
        toastSuccess("Offre approuvée");
        queryClient.invalidateQueries("getAllOffers");
      },
      onError() {
        toastError(general.fr.message.operationFailed);
      },
    });
  };
  const rejectOffer = (id: string) => {
    updateOfferStatus.mutate(`specials/${id}/reject`, {
      onSuccess() {
        toastSuccess("Offre rejetée");
        queryClient.invalidateQueries("getAllOffers");
      },
      onError() {
        toastError(general.fr.message.operationFailed);
      },
    });
  };

  const offerTitle = Offer?.title ?? Offer?.event?.name ?? "";
  // Adjust these field names if your offer model stores its image elsewhere
  const offerImage =
    (Offer as any)?.image ??
    (Offer as any)?.images?.[0]?.url ??
    (Offer as any)?.images?.[0] ??
    (Offer as any)?.event?.image ??
    `https://ui-avatars.com/api/?name=${encodeURIComponent(offerTitle)}`;

  const offerStatus = Offer?.status?.label;
  const approved = offerStatus === "approved";
  const rejected = offerStatus === "rejected";
  const pending = offerStatus?.toLowerCase() === "pending";

  return (
    <CTableRow
      style={{
        backgroundColor: isChecked ? "rgba(240,129,63,0.06)" : undefined,
        boxShadow: isChecked ? `inset 3px 0 0 ${colors.primary}` : undefined,
      }}
    >
      {/* Checkbox */}
      <CTableDataCell style={{ ...tdStyle, width: 44 }}>
        <CFormCheck
          checked={isChecked}
          onChange={(e) => setIsChecked(e.target.checked)}
        />
      </CTableDataCell>

      {/* Title */}
      <CTableDataCell style={tdStyle}>
        <div
          className="d-flex align-items-center gap-2"
          style={{ minWidth: 200 }}
        >
          <img
            src={offerImage}
            alt=""
            width={44}
            height={44}
            className="flex-shrink-0"
            style={{ borderRadius: 10, objectFit: "cover" }}
          />
          <div
            className="fw-semibold text-truncate"
            style={{ maxWidth: 260 }}
            title={offerTitle}
          >
            {offerTitle}
          </div>
        </div>
      </CTableDataCell>

      {/* Place (admin) */}
      {isAdmin && (
        <CTableDataCell style={tdStyle}>
          <span className="text-capitalize">
            {Offer?.place?.name ?? Offer?.event?.address ?? (
              <span className="text-muted">—</span>
            )}
          </span>
        </CTableDataCell>
      )}

      {/* Status */}
      <CTableDataCell style={tdStyle}>
        {approved ? (
          <Chip tone="success">✓ Approuvé</Chip>
        ) : pending ? (
          <Chip tone="warning">En attente</Chip>
        ) : (
          <Chip tone="danger">✕ Refusé</Chip>
        )}
      </CTableDataCell>

      {/* Discount */}
      <CTableDataCell style={tdStyle}>
        {Offer?.reductionPercent > 0 ? (
          <Chip tone="warning">-{Offer.reductionPercent}%</Chip>
        ) : (
          <span className="text-muted">—</span>
        )}
      </CTableDataCell>

      {/* Price */}
      <CTableDataCell style={tdStyle}>
        {Offer?.price > 0 ? (
          <span className="fw-bold">{Offer.price}</span>
        ) : (
          <span className="text-muted">—</span>
        )}
      </CTableDataCell>

      {/* Type */}
      <CTableDataCell style={tdStyle}>
        {/* @ts-ignore */}
        <Chip>{offersDisplayType[Offer?.type]}</Chip>
      </CTableDataCell>

      {/* Actions */}
      <CTableDataCell style={tdStyle}>
        <div className="d-flex align-items-center justify-content-end gap-2">
          {!approved && isAdmin && (
            <button
              disabled={updateOfferStatus.isLoading}
              onClick={() => approveOffer(Offer?.id)}
              className="btn btn-sm btn-primary rounded-pill text-white px-3"
            >
              Approuver
            </button>
          )}
          {!rejected && isAdmin && (
            <button
              disabled={updateOfferStatus.isLoading}
              onClick={() => rejectOffer(Offer?.id)}
              className="btn btn-sm btn-outline-danger rounded-pill px-3"
            >
              Refuser
            </button>
          )}

          {isMultiPlaceAdmin === true && (
            <IconAction
              title="Pourcentages"
              to={`/offers/percentages/${Offer.id}`}
            >
              <PercentIcon width="16" height="16" color={colors.green} />
            </IconAction>
          )}

          {isMultiPlaceAdmin === true && (
            <IconAction
              title="QR codes"
              to={`/place-admin-partners/qr-codes/offer/${Offer.id}`}
            >
              <QrCodeIcon width="16" height="16" color={colors.black} />
            </IconAction>
          )}

          <IconAction title="Modifier" to={getOfferUpdateLink(Offer)}>
            <EditIcon width="16" height="16" fill={colors.secondary} />
          </IconAction>

          {isAdmin && (
            <IconAction
              title="Traduire"
              to="/special-translations/new"
              state={{ offer: Offer }}
            >
              <TranslateIcon width="18" height="18" fill="#000" />
            </IconAction>
          )}

          <button
            type="button"
            title="Supprimer"
            aria-label="Supprimer"
            onClick={() => setShowModal(true)}
            disabled={updateOfferStatus.isLoading || deleteOffer.isLoading}
            className="d-inline-flex align-items-center justify-content-center border-0 rounded-circle"
            style={{ ...iconBtnStyle, backgroundColor: "rgba(220,53,69,0.10)" }}
          >
            {deleteOffer.isLoading ? (
              <CSpinner size="sm" />
            ) : (
              <DeleteIcon width="18" height="18" fill={colors.primary} />
            )}
          </button>
        </div>
      </CTableDataCell>

      <PromptModal
        open={showModal}
        onAccept={() => removeOffer()}
        onClose={() => setShowModal(false)}
      />
    </CTableRow>
  );
}

/* ─────────────── Table ─────────────── */

interface TableProps {
  offers: SpecialOffers[];
  OffersToBeDeleted: React.Dispatch<BulkDeleteActions>;
}

export default function OffersTable({ offers, OffersToBeDeleted }: TableProps) {
  const { isAdmin } = useAuth();
  return (
    <div className="mt-3">
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell style={{ ...thStyle, width: 44 }} />
            <CTableHeaderCell scope="col" style={thStyle}>
              Titre
            </CTableHeaderCell>
            {isAdmin && (
              <CTableHeaderCell scope="col" style={thStyle}>
                Établissement
              </CTableHeaderCell>
            )}
            <CTableHeaderCell scope="col" style={thStyle}>
              Statut
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Remise
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Prix
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Type
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle} />
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {offers?.map((offer) => (
            <TableBodyRowContent
              Offer={offer}
              key={offer?.id}
              deletedList={OffersToBeDeleted}
            />
          ))}
        </CTableBody>
      </CTable>
    </div>
  );
}
